import { Component, computed, ElementRef, input, model, output, Signal, ViewChild } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { TaskActivitiesListComponent } from '@app/components/task/task-timeline/task-activities-list/task-activities-list.component';
import { TaskTimelineCommentComponent } from '@app/components/task/task-timeline/timeline-comment/timeline-comment.component';
import { ActivityFilterMenuComponent } from '@app/shared/components/activity-filter-menu/activity-filter-menu.component';
import { TaskActivityType } from '@app/shared/types/enums/task-activity-type';
import { TaskActivity } from '@app/shared/types/models/task-activities/task-activity';
import { Task } from '@app/shared/types/models/task/task.model';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { UiDetailPanelComponent } from '@app/shared/ui/detail-panel/ui-detail-panel.component';
import { ActivityFiltersMap, getActionsActivityFiltersMap } from '@app/shared/utils/activity-utils';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-task-timeline',
  templateUrl: './task-timeline.component.html',
  styleUrl: './task-timeline.component.scss',
  imports: [
    TaskActivitiesListComponent,
    TaskTimelineCommentComponent,
    UiDetailPanelComponent,
    UiButtonComponent,
    TranslatePipe,
    MatMenuModule,
    ActivityFilterMenuComponent
  ],
  host: {
    class: 'flex w-full flex-col',
    '[class.h-full]': 'constrainHeight()',
    '[class.min-h-0]': 'constrainHeight()'
  }
})
export class TaskTimelineComponent {
  @ViewChild('activitiesContainer', { read: ElementRef })
  private readonly activitiesContainer!: ElementRef<HTMLElement>;

  protected readonly activityFilters = model<ActivityFiltersMap>(getActionsActivityFiltersMap());

  protected readonly selectedFiltersCount = computed(
    () => Object.values(this.activityFilters()).filter(Boolean).length
  );

  protected readonly filteredActivities: Signal<TaskActivity[]> = computed(() => {
    const activities = this.taskActivities();
    const filtersMap = this.activityFilters();
    const selectedKeys = (Object.entries(filtersMap) as [TaskActivityType, boolean][])
      .filter(([, selected]) => selected)
      .map(([key]) => key);

    return activities.filter((activity: TaskActivity) => selectedKeys.some(key => activity.taskActivityType === key));
  });

  protected readonly showPanelChrome = computed(() => this.constrainHeight());

  public readonly task = input.required<Task>();
  public readonly taskActivities = input.required<TaskActivity[]>();
  /** When false, the timeline grows with its content and uses the parent view scroll (mobile). */
  public readonly constrainHeight = input(true);
  public readonly showClose = input(true);
  public readonly panelClosed = output<void>();

  protected onPanelClosed(): void {
    this.panelClosed.emit();
  }

  protected scrollToBottom(): void {
    const activitiesContainer = this.activitiesContainer?.nativeElement;
    if (!activitiesContainer) {
      return;
    }

    if (this.constrainHeight()) {
      activitiesContainer.scrollTop = activitiesContainer.scrollHeight;
      return;
    }

    activitiesContainer.scrollIntoView({ block: 'end' });
  }
}
